import { Loader2 } from 'lucide-react'

import { cn } from './cn'

const LAYOUTS = {
  /** Fills the slot the finished screen will occupy. `min-h-64` is a floor:
   *  inside a scrolling parent there is no free space for `flex-1` to grow
   *  into, and the band would collapse under the spinner. */
  page: 'flex flex-col flex-1 min-h-64 items-center justify-center',
  /** A section waiting inside a screen that already renders content around it. */
  block: 'flex flex-col items-center justify-center py-20',
  /** A small region — a dropdown body, a lazy chunk, a panel. */
  inline: 'flex flex-col items-center justify-center py-6',
  /** In place of a control on a row, where a band of its own would open a gap. */
  row: 'flex flex-row items-center justify-center py-1',
  /** Covers the nearest positioned ancestor. Supply a ground via `className`. */
  overlay: 'absolute inset-0 z-50 flex flex-col items-center justify-center',
} as const

const SIZE = { small: 18, large: 28 } as const

type LoadingIndicatorProps = {
  layout?: keyof typeof LAYOUTS
  size?: keyof typeof SIZE
  text?: string
  /** Ground colour and any spacing the layout does not carry. */
  className?: string
}

/**
 * The one "this region is waiting on data" visual, shared by all three areas.
 *
 * For a placeholder that holds the shape of the content it replaces, write a
 * skeleton instead — `TableSkeleton` for tabular content.
 */
export function LoadingIndicator({
  layout = 'page',
  // A row is one line tall, so the large glyph does not fit in it.
  size = layout === 'row' ? 'small' : 'large',
  text,
  className,
}: LoadingIndicatorProps) {
  return (
    <div className={cn(LAYOUTS[layout], 'text-faint', className)}>
      <Loader2 size={SIZE[size]} className="animate-spin" aria-hidden />
      {text ? <p className="mt-3 text-sm">{text}</p> : null}
    </div>
  )
}
